import { Fragment, useState } from "react";
import InternalPage from "../../../components/layout/internal/InternalPage";
import { classNames } from "../../../utils/utils";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Link } from "react-router-dom";
import { formMappings } from "../../../config/formMappings";
import { formFields } from "../../../services/form_fields";

// Helper function to get field value (handles v1/v2 differences)
function getFieldValue(
  entry: any,
  fieldKey: string | undefined,
  fallbackKey?: string
): string {
  if (!fieldKey) return "";

  // Check if API service mapped it to human-readable name (v1)
  const humanReadableName = formFields[fieldKey]?.name;
  if (humanReadableName && entry.meta[humanReadableName]) {
    const value = entry.meta[humanReadableName];
    return Array.isArray(value) ? value.join(", ") : String(value || "");
  }

  // v2: Use raw field key
  const value = entry.meta[fieldKey];
  if (value !== undefined && value !== null) {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    return String(value);
  }

  // Try fallback key if provided
  if (fallbackKey && entry.meta[fallbackKey]) {
    const fallbackValue = entry.meta[fallbackKey];
    return Array.isArray(fallbackValue)
      ? fallbackValue.join(", ")
      : String(fallbackValue);
  }

  return "";
}

// Helper function to escape CSV values
function escapeCSV(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export default function AllRegistrationsPage() {
  const entries = useSelector(
    (state: RootState) => state.formEntries.allEntries
  );

  const activeFormName = useSelector(
    (state: RootState) => state.formEntries.activeFormName || ""
  );

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const fieldMappings = formMapping?.fieldMappings || {};
  const registrantsFieldKey = (fieldMappings as any)?.Registrants || "9v3kh";
  const isVersion1 = formMapping?.version === "1";
  const [isExporting, setIsExporting] = useState(false);

  // CSV Export function
  const handleExportCSV = () => {
    setIsExporting(true);

    // Use setTimeout to ensure the UI updates before the heavy processing
    setTimeout(() => {
      const csvRows: string[] = [];

      // Define CSV headers
      const headers = [
        "Registration ID",
        "Paid Status",
        "First Name",
        "Last Name",
        "Gender",
        "Age",
        "Email",
        "Registration Type",
        "Ethnicity",
        "Programs",
        "Phone",
        "City",
        "State",
        "Staying in Hotel",
        "Last Time Attended",
        "Hear about Conference",
        "Getting to Conference by",
        "Most Active Social Media",
        "Food Allergies",
        "Special Accommodations",
        "Emergency Contact Name",
        "Emergency Contact Phone",
        "Total",
        "Number of Registrants",
        "Checked In",
        "Created At",
      ];

      csvRows.push(headers.map(escapeCSV).join(","));

      // Process each entry
      entries
        .slice()
        .reverse()
        .forEach((entry) => {
          // Get supplementary information from entry.meta
          let paidStatus = getFieldValue(entry, fieldMappings.paid, "Paid?");
          if (!paidStatus) {
            // Try fallback field keys directly
            paidStatus =
              entry.meta["Paid?"] ||
              entry.meta["fmz5v32"] ||
              entry.meta["fmz5v3"] ||
              "";
            if (Array.isArray(paidStatus)) {
              paidStatus = paidStatus[0] || "";
            }
            paidStatus = String(paidStatus);
          }
          const paidStatusDisplay = paidStatus === "Yes" ? "Paid" : "Not Paid";

          const phone = getFieldValue(entry, fieldMappings.phone);

          let city = "";
          let state = "";
          if (isVersion1) {
            const addressObjectKey = (fieldMappings as any)?.address_object;
            if (addressObjectKey && entry.meta[addressObjectKey]) {
              const addressObject = entry.meta[addressObjectKey] as any;
              if (addressObject && typeof addressObject === "object") {
                city = addressObject.city || "";
                state = addressObject.state || "";
              }
            }
          } else {
            city = getFieldValue(entry, fieldMappings.city);
            state = getFieldValue(entry, fieldMappings.state);
          }

          const stayingInHotel = getFieldValue(
            entry,
            fieldMappings["Staying in Hotel"]
          );
          const lastTimeAttended = getFieldValue(
            entry,
            fieldMappings["Last Time Attended"]
          );
          const hearAboutConference = getFieldValue(
            entry,
            fieldMappings["Hear about Conference"]
          );
          const gettingToConference = getFieldValue(
            entry,
            fieldMappings["Getting to Conference by"]
          );
          const mostActiveSocialMedia = getFieldValue(
            entry,
            fieldMappings["Most Active Social Media"]
          );
          const foodAllergies = getFieldValue(
            entry,
            fieldMappings["Food Allergies"]
          );
          const specialAccommodations = getFieldValue(
            entry,
            fieldMappings["Special Accommodations"]
          );
          const emergencyContactName = getFieldValue(
            entry,
            fieldMappings["Emergency Contact Name"]
          );
          const emergencyContactPhone = getFieldValue(
            entry,
            fieldMappings["Emergency Contact Phone"]
          );
          const total = getFieldValue(entry, fieldMappings.Total);
          const numberOfRegistrants = getFieldValue(
            entry,
            fieldMappings["Number of Registrants"]
          );

          // Get ethnicity for v1 (stored at entry level)
          let entryEthnicity = "";
          if (isVersion1) {
            const ethnicityValue = entry.meta["Ethnicity"];
            if (ethnicityValue) {
              if (Array.isArray(ethnicityValue)) {
                entryEthnicity = ethnicityValue.join(", ");
              } else {
                entryEthnicity = String(ethnicityValue);
              }
            }
          }

          // Get registrants
          let registrants: any = null;
          if (entry.meta.Registrants) {
            registrants = entry.meta.Registrants;
          } else {
            const basePrefix = registrantsFieldKey.replace(/\d+$/, "");
            const registrantsKey = Object.keys(entry.meta).find(
              (key) =>
                (key === registrantsFieldKey || key.startsWith(basePrefix)) &&
                typeof entry.meta[key] === "object"
            );
            if (registrantsKey) {
              registrants = entry.meta[registrantsKey];
            }
          }

          if (!registrants) {
            // If no registrants, still add a row with just the supplementary info
            const row = [
              entry.item_key,
              paidStatusDisplay,
              "", // First Name
              "", // Last Name
              "", // Gender
              "", // Age
              "", // Email
              "", // Registration Type
              entryEthnicity, // Ethnicity (v1 only, empty for v2)
              "", // Programs
              phone,
              city,
              state,
              stayingInHotel,
              lastTimeAttended,
              hearAboutConference,
              gettingToConference,
              mostActiveSocialMedia,
              foodAllergies,
              specialAccommodations,
              emergencyContactName,
              emergencyContactPhone,
              total,
              numberOfRegistrants,
              "", // Checked In (empty for entries with no registrants)
              entry.created_at || "",
            ];
            csvRows.push(row.map(escapeCSV).join(","));
            return;
          }

          // Create a row for each registrant
          Object.entries(registrants)
            .filter(([key]) => key !== "form")
            .forEach(([_, registrant]) => {
              const firstNameKey = fieldMappings.firstName || "s7ztq3";
              const lastNameKey = fieldMappings.lastName || "pu0bc3";
              const genderKey = fieldMappings.gender || "85tfd3";
              const ageKey = fieldMappings.Age || "i6b3v3";
              const emailKey = fieldMappings.email || "jgfy3";
              const memberParticipationKey =
                fieldMappings["Member Participation"] || "bpc1r3";
              // Get registration type field key (v1: 5yusq3, v2: 5yusq32)
              const registrationTypeKey = isVersion1 ? "5yusq3" : "5yusq32";
              // Get checked-in field key (v1: opjkc3, v2: opjkc32)
              const checkedInFieldKey =
                fieldMappings["Checked In"] ||
                (isVersion1 ? "opjkc3" : "opjkc32");
              // Get ethnicity field key (v2: b3bic3, v1: stored at entry level)
              const ethnicityFieldKey =
                (fieldMappings as any).Ethnicity || "b3bic3";

              const firstName = (registrant as any)[firstNameKey] || "";
              const lastName = (registrant as any)[lastNameKey] || "";
              const gender = (registrant as any)[genderKey] || "";
              const age = (registrant as any)[ageKey] || "";
              const email = (registrant as any)[emailKey] || "";
              const registrationType =
                (registrant as any)[registrationTypeKey] || "";
              // Get ethnicity - v2 from registrant, v1 from entry level
              let ethnicity = "";
              if (isVersion1) {
                ethnicity = entryEthnicity;
              } else {
                const ethnicityValue = (registrant as any)[ethnicityFieldKey];
                if (ethnicityValue) {
                  if (Array.isArray(ethnicityValue)) {
                    ethnicity = ethnicityValue.join(", ");
                  } else {
                    ethnicity = String(ethnicityValue);
                  }
                }
              }
              const programs = Array.isArray(
                (registrant as any)[memberParticipationKey]
              )
                ? (registrant as any)[memberParticipationKey].join(", ")
                : (registrant as any)[memberParticipationKey] || "";
              // Get checked-in status from the registrant object
              const checkedIn = (registrant as any)[checkedInFieldKey] || "";
              const checkedInDisplay =
                checkedIn === "true"
                  ? "Yes"
                  : checkedIn === "false"
                  ? "No"
                  : checkedIn;

              const row = [
                entry.item_key,
                paidStatusDisplay,
                firstName,
                lastName,
                gender,
                age,
                email,
                registrationType,
                ethnicity,
                programs,
                phone,
                city,
                state,
                stayingInHotel,
                lastTimeAttended,
                hearAboutConference,
                gettingToConference,
                mostActiveSocialMedia,
                foodAllergies,
                specialAccommodations,
                emergencyContactName,
                emergencyContactPhone,
                total,
                numberOfRegistrants,
                checkedInDisplay,
                entry.created_at || "",
              ];

              csvRows.push(row.map(escapeCSV).join(","));
            });
        });

      // Create CSV content and download
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `registrations_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(url);

      // Reset loading state after a short delay to ensure download started
      setTimeout(() => {
        setIsExporting(false);
      }, 500);
    }, 0);
  };

  return (
    <InternalPage>
      <div className="px-0 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">
              All Registrants
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the registrants (paid or unpaid) including their
              name, gender, age, email and other registration information.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isExporting}
              className={classNames(
                "block rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                isExporting
                  ? "bg-primary/70 cursor-not-allowed"
                  : "bg-primary hover:bg-secondary focus-visible:outline-primary"
              )}
            >
              <span className="flex items-center justify-center gap-2">
                {isExporting && (
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {isExporting ? "Exporting..." : "Export to CSV"}
              </span>
            </button>
          </div>
        </div>
        <div className="mt-8 flow-root rounded-md bg-white">
          <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle sm:px-6 lg:px-8">
              <table className="min-w-full">
                <thead className="bg-white">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Gender
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Age
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Programs
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                    >
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {entries
                    .slice()
                    .reverse()
                    .map((entry) => (
                      <Fragment key={entry.item_key}>
                        <tr className="border-t border-gray-200">
                          <th
                            scope="colgroup"
                            colSpan={6}
                            className="bg-gray-50 py-2 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                          >
                            <div className="flex items-center justify-between">
                              <span>{entry.item_key}</span>
                              {(() => {
                                // Get paid status (try human-readable name first, then field keys)
                                const paidStatus =
                                  entry.meta["Paid?"] ||
                                  entry.meta["fmz5v32"] ||
                                  entry.meta["fmz5v3"] ||
                                  "";
                                return paidStatus === "Yes" ? (
                                  <span className="rounded-md bg-green-50 px-2 py-1 text-md font-medium text-green-600 ring-1 ring-inset ring-green-600/20">
                                    Paid
                                  </span>
                                ) : (
                                  <span className="rounded-md bg-red-50 px-2 py-1 text-md font-medium text-red-600 ring-1 ring-inset ring-red-600/20">
                                    Not Paid
                                  </span>
                                );
                              })()}
                            </div>
                          </th>
                        </tr>
                        {/* Get registrants from mapped key or fallback to registrants container */}
                        {(() => {
                          let registrants: any = null;
                          if (entry.meta.Registrants) {
                            registrants = entry.meta.Registrants;
                          } else {
                            // Fallback: look for registrants using the Registrants field key from formMappings
                            const basePrefix = registrantsFieldKey.replace(
                              /\d+$/,
                              ""
                            );
                            const registrantsKey = Object.keys(entry.meta).find(
                              (key) =>
                                (key === registrantsFieldKey ||
                                  key.startsWith(basePrefix)) &&
                                typeof entry.meta[key] === "object"
                            );
                            if (registrantsKey) {
                              registrants = entry.meta[registrantsKey];
                            }
                          }

                          if (!registrants) return null;

                          return Object.entries(registrants)
                            .filter(([key]) => key !== "form")
                            .map(([_, registrant], registrantIdx) => {
                              const firstNameKey =
                                fieldMappings.firstName || "s7ztq3";
                              const lastNameKey =
                                fieldMappings.lastName || "pu0bc3";
                              const genderKey =
                                fieldMappings.gender || "85tfd3";
                              const ageKey = fieldMappings.Age || "i6b3v3";
                              const emailKey = fieldMappings.email || "jgfy3";
                              const memberParticipationKey =
                                fieldMappings["Member Participation"] ||
                                "bpc1r3";

                              return (
                                <tr
                                  key={registrantIdx}
                                  className={classNames(
                                    registrantIdx === 0
                                      ? "border-gray-300"
                                      : "border-gray-200",
                                    "border-t"
                                  )}
                                >
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                                    {`${
                                      (registrant as any)[firstNameKey] || ""
                                    } ${
                                      (registrant as any)[lastNameKey] || ""
                                    }`}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {(registrant as any)[genderKey] || ""}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {(registrant as any)[ageKey] || ""}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {(registrant as any)[emailKey] || ""}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {Array.isArray(
                                      (registrant as any)[
                                        memberParticipationKey
                                      ]
                                    )
                                      ? (registrant as any)[
                                          memberParticipationKey
                                        ].join(", ")
                                      : (registrant as any)[
                                          memberParticipationKey
                                        ] || ""}
                                  </td>
                                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                                    <Link
                                      to={"/registrant/" + entry.item_key}
                                      className="text-primary hover:text-secondary"
                                    >
                                      View
                                      <span className="sr-only">
                                        , {(registrant as any)[genderKey] || ""}
                                      </span>
                                    </Link>
                                  </td>
                                </tr>
                              );
                            });
                        })()}
                      </Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </InternalPage>
  );
}
